import { SHOOTING_SETTINGS, LANGUAGES, type CreatorFilters } from "@/lib/creator-filters";
import { useTranslation } from "react-i18next";
import { useState } from "react";

export type FilterOption = { value: string; label: string };

type Props = {
  values: Partial<CreatorFilters>;
  onChange: (patch: Partial<CreatorFilters>) => void;
  onReset?: () => void;
  resultsCount: number;
  /** Which selects to render. */
  show: {
    country?: FilterOption[];
    city?: FilterOption[];
    theme?: FilterOption[];
    price?: FilterOption[];
    setting?: boolean;
    language?: boolean;
  };
};

const selectClass =
  "bg-transparent border-b border-foreground py-1 pr-6 max-w-[180px] truncate";

function Field({
  label,
  value,
  onChange,
  options,
  ariaLabel,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: FilterOption[];
  ariaLabel: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3">
      <span className="text-muted-foreground">{label}</span>
      <select
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={selectClass}
      >
        <option value="">{t("filters.all")}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function CreatorFilterBar({ values, onChange, onReset, resultsCount, show }: Props) {
  const { t } = useTranslation();
  const activeCount = [
    values.country,
    values.city,
    values.theme,
    values.price,
    values.setting,
    values.language,
  ].filter(Boolean).length;
  const hasActive = activeCount > 0;
  const [open, setOpen] = useState(false);

  return (
    <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-6 eyebrow">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="flex items-center gap-2 text-foreground hover:text-muted-foreground transition-colors"
        >
          <span>{t("filters.toggle")}</span>
          {activeCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-5 h-5 px-1 bg-foreground text-background rounded-full text-[10px]">
              {activeCount}
            </span>
          )}
          <span className={`transition-transform ${open ? "rotate-45" : ""}`}>+</span>
        </button>
        <div className="ml-auto flex items-center gap-4">
          {hasActive && onReset && (
            <button
              type="button"
              onClick={onReset}
              className="text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
            >
              {t("filters.reset")}
            </button>
          )}
          <span className="text-muted-foreground">{t("filters.results", { count: resultsCount })}</span>
        </div>
      </div>

      {open && (
        <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-4 border-t border-border pt-6">
      {show.country && (
        <Field
          label={t("filters.country")}
          ariaLabel={t("filters.ariaCountry")}
          value={values.country ?? ""}
          onChange={(v) => onChange({ country: v, city: "" })}
          options={show.country}
        />
      )}
      {show.city && (
        <Field
          label={t("filters.city")}
          ariaLabel={t("filters.ariaCity")}
          value={values.city ?? ""}
          onChange={(v) => onChange({ city: v })}
          options={show.city}
        />
      )}
      {show.theme && (
        <Field
          label={t("filters.category")}
          ariaLabel={t("filters.ariaCategory")}
          value={values.theme ?? ""}
          onChange={(v) => onChange({ theme: v })}
          options={show.theme}
        />
      )}
      {show.price && (
        <Field
          label={t("filters.price")}
          ariaLabel={t("filters.ariaPrice")}
          value={values.price ?? ""}
          onChange={(v) => onChange({ price: v })}
          options={show.price}
        />
      )}
      {show.setting && (
        <Field
          label={t("filters.setting")}
          ariaLabel={t("filters.ariaSetting")}
          value={values.setting ?? ""}
          onChange={(v) => onChange({ setting: v })}
          options={SHOOTING_SETTINGS.map((s) => ({ value: s, label: s }))}
        />
      )}
      {show.language && (
        <Field
          label={t("filters.language")}
          ariaLabel={t("filters.ariaLanguage")}
          value={values.language ?? ""}
          onChange={(v) => onChange({ language: v })}
          options={LANGUAGES.map((l) => ({ value: l, label: l }))}
        />
      )}
        </div>
      )}
    </div>
  );
}